import { toast } from 'sonner';
import { loadSoundSettings } from '@/lib/notification-sound-settings';
import i18n from '@/i18n/config';
import type { NotificationPayload } from '@/types/notifications';

const NOTIFICATION_SOUND_URL = '/sounds/universfield-new-notification-020-352772.mp3';
let notificationAudio: HTMLAudioElement | null = null;
let audioUnlocked = false;

function ensureAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!notificationAudio) {
    notificationAudio = new Audio(NOTIFICATION_SOUND_URL);
    notificationAudio.volume = 0.6;
    notificationAudio.preload = 'auto';
  }
  return notificationAudio;
}

/**
 * Browsers block `Audio.play()` until the user has interacted with the page, and a notification
 * fired from the SSE stream never happens inside a user gesture — so the sound was silently
 * blocked forever. Prime the element on the FIRST pointer/key interaction (play it muted, then
 * reset) so later, event-driven plays are allowed. Call once, early.
 */
export function initNotificationAudioUnlock() {
  if (typeof window === 'undefined' || audioUnlocked) return;
  const unlock = () => {
    const audio = ensureAudio();
    if (!audio) return;
    audio.muted = true;
    audio
      .play()
      .then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        audioUnlocked = true;
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
      })
      .catch(() => {
        audio.muted = false;
      });
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
}

let lastPlayedAt = 0;
let ringSession = 0;

/** One chime, resolved when it finishes (or immediately if playback is unavailable). */
function playOnce(audio: HTMLAudioElement): Promise<void> {
  return new Promise((resolve) => {
    const done = () => {
      audio.removeEventListener('ended', done);
      resolve();
    };
    audio.addEventListener('ended', done);
    try {
      audio.currentTime = 0;
      // Should be unlocked by initNotificationAudioUnlock(); swallow the rejection just in case
      // (e.g. the user hasn't interacted yet) so the visual notification still fires.
      void audio.play().catch(done);
    } catch {
      done();
    }
  });
}

/**
 * Ring the notification chime according to the operator's sound settings (enabled, number of
 * repeats, volume — see notification-sound-settings.ts). Exported so the conversation list can
 * ring it directly when it detects a handoff via its 2s poll — the poll is the delivery path we
 * KNOW reaches this operator (it's what paints the row red), independent of the SSE stream's
 * client-targeting. A short dedupe window keeps the SSE handler and the poll from double-ringing
 * the same event; a new event supersedes any repeat sequence still ringing.
 *
 * `force` (the settings panel's "test" button) bypasses the enabled check and the dedupe window.
 */
export function playNotificationSound(force = false) {
  const settings = loadSoundSettings();
  if (!settings.enabled && !force) return;
  const audio = ensureAudio();
  if (!audio) return;
  const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!force && now - lastPlayedAt < 3000) return;
  lastPlayedAt = now;
  audio.volume = settings.volume;

  const session = ++ringSession;
  void (async () => {
    for (let i = 0; i < settings.repeats; i++) {
      if (session !== ringSession) return; // a newer notification took over the speaker
      await playOnce(audio);
      if (i < settings.repeats - 1) await new Promise((r) => setTimeout(r, 350));
    }
  })();
}

let persistentTimer: ReturnType<typeof setInterval> | null = null;

/** How long between bursts while conversations are still waiting for a human. */
const PERSISTENT_RING_INTERVAL_MS = 15_000;

/**
 * Keep ringing until someone attends the queue — the "suena hasta que una persona intervenga"
 * mode. Rings immediately, then every {@link PERSISTENT_RING_INTERVAL_MS} until
 * {@link stopPersistentRing} is called (the caller does that the moment the last pending handoff
 * clears). Safe to call repeatedly: while a cycle is already running this is a no-op, so the
 * 2-second inbox poll driving it never stacks timers or shortens the gap.
 */
export function startPersistentRing() {
  if (persistentTimer !== null) return;
  if (!loadSoundSettings().enabled) return;
  playNotificationSound(true);
  persistentTimer = setInterval(() => {
    // Re-read each cycle so switching the mode off in the panel silences it without a reload.
    const settings = loadSoundSettings();
    if (!settings.enabled || !settings.untilAttended) {
      stopPersistentRing();
      return;
    }
    playNotificationSound(true);
  }, PERSISTENT_RING_INTERVAL_MS);
}

export function stopPersistentRing() {
  if (persistentTimer === null) return;
  clearInterval(persistentTimer);
  persistentTimer = null;
}

export async function ensureNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

function destinationFor(payload: NotificationPayload): string | null {
  if (payload.type === 'new_order') return '/orders';
  if (
    payload.type === 'conversation_switched_to_manual' ||
    payload.type === 'conversation_needs_intervention'
  ) {
    return payload.data?.conversationId ? `/?conversationId=${payload.data.conversationId}` : '/';
  }
  return null;
}

export function showNotification(payload: NotificationPayload) {
  const destination = destinationFor(payload);

  playNotificationSound();

  toast(payload.title, {
    description: payload.body,
    ...(destination
      ? {
          action: {
            label: i18n.t('app:notifications.view'),
            onClick: () => {
              window.location.href = destination;
            },
          },
        }
      : {}),
  });

  if (document.visibilityState === 'visible' || Notification.permission !== 'granted') return;

  const n = new Notification(payload.title, {
    body: payload.body,
    tag: payload.type,
  });
  n.onclick = () => {
    window.focus();
    if (destination) {
      window.location.href = destination;
    }
    n.close();
  };
}
